import { reportClientError as sendClientError } from '@/api/feedback.api'
import { useAuthStore } from '@/stores/auth'
import { isBenignClientError } from '@/utils/benignClientErrors'

function normalizeError(error: unknown): { message: string, stack?: string } | null {
    if (error == null) {
        return null
    }

    if (error instanceof Error) {
        const message = (error.message || error.name || '').trim()
        if (!message) {
            return null
        }
        return {
            message,
            stack: error.stack,
        }
    }

    if (typeof error === 'string') {
        const message = error.trim()
        if (!message) {
            return null
        }
        return { message }
    }

    try {
        const message = JSON.stringify(error).trim()
        if (!message || message === 'undefined' || message === 'null') {
            return null
        }
        return { message }
    }
    catch {
        const message = String(error).trim()
        if (!message) {
            return null
        }
        return { message }
    }
}

function getUserContext(): Record<string, string> {
    try {
        const user = useAuthStore().currentUser
        if (!user?.id) {
            return {}
        }

        return {
            user_id: String(user.id),
            ...(user.email ? { user_email: String(user.email) } : {}),
        }
    }
    catch {
        return {}
    }
}

export function reportClientError(error: unknown, context?: Record<string, unknown>) {
    const normalized = normalizeError(error)
    if (!normalized) {
        return
    }
    if (isBenignClientError(normalized.message, normalized.stack)) {
        return
    }

    void sendClientError({
        message: normalized.message,
        stack: normalized.stack,
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        context: {
            ...getUserContext(),
            ...context,
        },
    }).catch(() => {
        // Avoid recursive error reporting if the alert endpoint fails.
    })
}
